import MailIcon from '@mui/icons-material/Mail'
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import React, { useContext, useState } from 'react'
import './EnquiryItem.css'
import NiceModal from '@ebay/nice-modal-react'
import { Lock, LockOpenOutlined, LockOutlined, Message, MessageOutlined } from '@mui/icons-material'
import { useIntersectionObserver } from '@uidotdev/usehooks'
import { GlobalContext } from '../context/GlobalContext'
import useApi from '../hooks/useApi'
import haxios from '../lib/haxios'
import { Spinner } from './gsys-ui/Spinner/Spinner'
import clsx from 'clsx'
import { useEnquiryLock } from '../stores/useLockStore'
import useLockStore from '../stores/useLockStore'
import ConfirmationModal from './ConfirmationModal'

dayjs.extend(relativeTime)

const EnquiryItem = ({
  backorderStatus,
  enquiry,
  onMessageClick,
  refetch,
  isHighlighted = false
}) => {
  const [isModalLoading, setModalLoading] = useState(false)
  const [unlockLoading, setUnlockLoading] = useState(false)
  const { cinfo } = useContext(GlobalContext)
  const { lockState, connected, lock, unlock } = useEnquiryLock(enquiry.EnquiryId)
  const { forceReconnect } = useLockStore()
  const isLocked = lockState && lockState.username !== cinfo.Username && cinfo.Admin === false

  const isRegPlate = enquiry.Reg && enquiry.Reg !== 'CONSUMABLES'

  const today = dayjs().startOf('day')
  const createdDate = dayjs(enquiry.DatetimeCreated)
  const modifiedDate = dayjs(enquiry.DatetimeModified)

  const isCreatedToday = createdDate.isSame(today, 'day')
  const isModifiedToday = modifiedDate.isSame(today, 'day')

  const formattedCreatedDate = isCreatedToday
    ? 'Today, ' + createdDate.format('HH:mm')
    : createdDate.format('DD/MM/YYYY, HH:mm')
  const formattedModifiedDate = isModifiedToday
    ? 'Today, ' + modifiedDate.format('HH:mm')
    : modifiedDate.format('DD/MM/YYYY, HH:mm')

  const timeSinceCreated = createdDate.fromNow()
  const timeSinceModified = modifiedDate.fromNow()

  const handleShowEnquiry = async () => {
    if (isLocked || isModalLoading) return // Prevent multiple clicks
    setModalLoading(true)

    try {
      if (!lockState) {
        console.log('🔒 Attempting to lock enquiry:', enquiry.EnquiryId)
        const res = await lock()
        console.log('🔒 Lock result:', res)
      }

      NiceModal.show('enquiryDetailsModal', {
        enquiry,
        currentUser: cinfo.Username,
        refetchEnquiries: refetch
      })

      refetch()
    } catch (error) {
      console.error('Error opening enquiry details:', error)
      // If we get a connection error, try to force reconnect
      if (error.message.includes('Not connected') || error.message.includes('Connection')) {
        console.log('🔄 Connection error detected - attempting force reconnect')
        forceReconnect()
      } else if (error.message.includes('timeout')) {
        console.log('⏰ Lock request timed out - connection may be slow')
        // Don't force reconnect for timeouts, just show error
      }
    } finally {
      setModalLoading(false)
    }
  }

  const handleAdminUnlock = async () => {
    setUnlockLoading(true)
    await unlock()
    setUnlockLoading(false)
  }

  const getStatusColor = (status) => {
    const colorMap = {
      'Waiting': '#FF6B35',
      'Order': '#E91E63',
      'Ordered': '#2ECC71',
      'Garage Confirmed': '#E91E63',
      'Quoted': '#3498DB',
      'Cancelled': '#E74C3C',
      'Archived': '#95A5A6'
    }
    return colorMap[status] || '#FF6B35'
  }

  const getStatusLabel = (status) => {
    const labels = {
      'Waiting': 'TO QUOTE',
      'Ordered': 'ORDERED',
      'Quoted': 'QUOTED',
      'Order': 'DIRECT ORDER',
      'Garage Confirmed': 'CONFIRMED',
      'Cancelled': 'CANCELLED'
    }
    return labels[status] || status
  }

  const statusColor = getStatusColor(enquiry.Status)

  // Determine backorder status and visual indicators
  const getBackorderInfo = () => {
    if (!backorderStatus) {
      // Only log for enquiry 22259
      if (enquiry.Document === 22259) {
        console.log('[22259] EnquiryItem: No backorderStatus received')
      }
      return { status: 'unknown', color: '#95A5A6', label: 'Unknown', icon: '❓' }
    }

    // Only log for enquiry 22259
    if (enquiry.Document === 22259) {
      console.log('[22259] EnquiryItem received backorderStatus:', {
        summary: backorderStatus.summary,
        lineItemsCount: backorderStatus.lineItems?.length,
        hasPartial: backorderStatus.summary?.partiallyBackordered > 0,
        hasFull: backorderStatus.summary?.fullyBackordered > 0
      })
    }
    const { summary, lineItems } = backorderStatus

    // Check lineItems first for more accurate status detection
    const hasFullyBackorderedItems =
      lineItems && lineItems.some((item) => item.status === 'fully_backordered')
    const hasPartiallyBackorderedItems =
      lineItems && lineItems.some((item) => item.status === 'partially_backordered')

    // Check summary counts
    const fullyBackorderedCount = summary?.totalBackorderDocuments || summary?.fullyBackordered || 0
    const partiallyBackorderedCount = summary?.partiallyBackordered || 0

    // Determine status - check both summary and lineItems
    // ONLY show backorder status (partial or full) - don't show fulfilled
    // Logic:
    // - If any item is partially_backordered → Partial Backorder
    // - If any item is fully_backordered AND any item is fulfilled → Partial Backorder (order partially fulfilled)
    // - If all items are fully_backordered → Full Backorder
    const hasFulfilledItems = lineItems && lineItems.some((item) => item.status === 'fulfilled')
    const isPartiallyBackordered = hasPartiallyBackorderedItems || partiallyBackorderedCount > 0
    const isFullyBackordered = hasFullyBackorderedItems || fullyBackorderedCount > 0

    // If any item is partially backordered, or if there's a mix of fulfilled and backordered → show partial
    if (isPartiallyBackordered || (isFullyBackordered && hasFulfilledItems)) {
      // Count backordered items (both partial and full) if there are also fulfilled items
      const backorderedCount = lineItems
        ? lineItems.filter(
            (item) => item.status === 'partially_backordered' || item.status === 'fully_backordered'
          ).length
        : (partiallyBackorderedCount || 0) + (fullyBackorderedCount || 0)

      return {
        status: 'partial',
        color: '#F39C12',
        label: 'Partial Backorder',
        icon: '⚠️',
        count: backorderedCount || partiallyBackorderedCount || fullyBackorderedCount || 0
      }
    } else if (isFullyBackordered) {
      // Count fully backordered items from lineItems if available
      const fullCount = lineItems
        ? lineItems.filter((item) => item.status === 'fully_backordered').length
        : fullyBackorderedCount || 0

      return {
        status: 'full',
        color: '#9B59B6',
        label: 'Full Backorder',
        icon: '🔄',
        count: fullCount || summary?.totalLines || fullyBackorderedCount || 0
      }
    }

    // No backorder status - return unknown (don't show fulfilled)
    return { status: 'unknown', color: '#95A5A6', label: 'No Data', icon: '❓' }
  }

  const backorderInfo = getBackorderInfo()

  return (
    <div
      className={clsx(
        'mb-3 rounded-mobile shadow-mobile border-2 overflow-hidden',
        isHighlighted && 'mt-2'
      )}
      style={{
        borderColor: statusColor,
        backgroundColor: 'rgba(52, 73, 94, 0.3)'
      }}
    >
      {/* Status Bar */}
      <Box
        sx={{
          backgroundColor: statusColor,
          color: 'white',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          minHeight: '40px'
        }}
      >
        {/* Left section: Document number and backorder info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              padding: '4px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: '1em',
                color: 'white'
              }}
            >
              {enquiry.Document}
            </Typography>
          </Box>
          {enquiry.isHistorical && (
            <Tooltip title="Historical priority enquiry (updates may take up to 10 minutes)" arrow>
              <Chip
                label="Historical"
                size="small"
                sx={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '22px',
                  fontWeight: 600
                }}
              />
            </Tooltip>
          )}
          {backorderInfo.status !== 'unknown' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: backorderInfo.color,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.75em',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <span>{backorderInfo.icon}</span>
                {backorderInfo.label}
                {backorderInfo.count && ` (${backorderInfo.count})`}
              </Typography>
            </Box>
          )}
        </Box>
        {/* Center section: Enquiry type */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 900,
              fontSize: '1.3em',
              letterSpacing: '1px',
              textAlign: 'center',
              textTransform: 'uppercase'
            }}
          >
            {getStatusLabel(enquiry.Status)}
          </Typography>
        </Box>
        {/* Right section: Message and lock icons */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto', flexShrink: 0 }}
        >
          <MessagesIcon
            messageCount={enquiry.MessageCount}
            factorUnread={enquiry.FactorUnread}
            onMessageClick={() =>
              NiceModal.show('enquiryDetailsModal', {
                initialTab: 1,
                refetchEnquiries: refetch,
                currentUser: cinfo.Username,
                enquiry
              })
            }
          />
          <LockedChip enquiry={enquiry} />
        </Box>
      </Box>

      {/* Card Content */}
      <Box sx={{ padding: '16px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {`${enquiry.CustomerKeyCode} - ${enquiry.GarageName}`}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ paddingRight: 2 }}>
              {isRegPlate ? <RegPlate size={1.08}>{enquiry.Reg}</RegPlate> : <ConsumablesBox />}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" sx={{ lineHeight: 1 }}>
                {enquiry.Parts}
              </Typography>
              <Typography variant="caption">
                {enquiry.Notes || 'No additional notes provided.'}
              </Typography>
              {enquiry.Status === 'Cancelled' && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#E74C3C',
                    fontWeight: '600',
                    display: 'block',
                    mt: 0.5
                  }}
                >
                  Cancelled: {enquiry.ResponseHeaderNotes || enquiry.Notes || 'No reason'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" sx={{ lineHeight: 1 }}>
                <span style={{ fontWeight: 600 }}>Created:</span> {formattedCreatedDate} (
                {timeSinceCreated})
              </Typography>
            </Box>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" sx={{ lineHeight: 1 }}>
                <span style={{ fontWeight: 600 }}>Modified:</span>{' '}
                {formattedModifiedDate === 'Invalid Date' ? 'Never' : formattedModifiedDate}{' '}
                {formattedModifiedDate === 'Invalid Date' ? '' : `(${timeSinceModified})`}
              </Typography>
            </Box>
          </Box>

          <Box>
            {lockState && cinfo.Admin && (
              <Button
                variant="outlined"
                onClick={handleAdminUnlock}
                disabled={unlockLoading}
                sx={{ marginRight: 2 }}
              >
                Unlock
              </Button>
            )}
            {enquiry.Status !== 'Cancelled' && enquiry.Status !== 'Ordered' && (
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  NiceModal.show(ConfirmationModal, {
                    enquiryId: enquiry.EnquiryId,
                    onCancelComplete: () => refetch()
                  })
                }
                disabled={isLocked}
                sx={{ marginRight: 2 }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleShowEnquiry}
              disabled={isLocked || isModalLoading || (!connected && !isModalLoading)}
              title={!connected ? 'Click to retry connection' : isModalLoading ? 'Loading...' : ''}
            >
              {isLocked
                ? 'Locked'
                : isModalLoading
                ? 'Loading...'
                : !connected
                ? 'Retry Connection'
                : 'View details'}
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export const LockedChip = ({ enquiry }) => {
  const { lockState, connected, lock } = useEnquiryLock(enquiry.EnquiryId)
  const isLocked = !!lockState
  const username = isLocked ? lockState.username : null

  return (
    <Box>
      <Tooltip title={`This enquiry is ${isLocked ? 'locked by ' + username : 'unlocked'}.`}>
        <Box
          sx={{
            background: isLocked ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            display: 'inline-flex',
            borderRadius: '12px',
            height: '24px',
            minWidth: '24px',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 6px',
            border: `1px solid rgba(255, 255, 255, 0.3)`,
            fontSize: '0.8em'
          }}
        >
          {isLocked ? (
            <LockOutlined sx={{ fontSize: '14px' }} />
          ) : (
            <LockOpenOutlined sx={{ fontSize: '14px' }} />
          )}
          {isLocked && username && (
            <Box sx={{ fontSize: '12px', fontWeight: 600, marginLeft: 0.5 }}>{username}</Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  )
}

export const UnlockedChip = () => {
  return (
    <Box>
      <Tooltip title="This enquiry is locked.">
        <Box
          sx={{
            background: '#2ECC71',
            color: 'white',
            display: 'inline-flex',
            borderRadius: '16px',
            height: '32px',
            minWidth: '32px',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid #2ECC71',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <LockOpenOutlined />
        </Box>
      </Tooltip>
    </Box>
  )
}

export const StatusChip = ({ status }) => {
  const colors = {
    'Waiting': '#FF6B35',
    'Ordered': '#2ECC71',
    'Order': '#E91E63',
    'Quoted': '#3498DB',
    'Garage Confirmed': '#E91E63',
    'Cancelled': '#E74C3C'
  }

  const labels = {
    'Waiting': 'TO QUOTE',
    'Ordered': 'ORDERED',
    'Quoted': 'QUOTED',
    'Order': 'DIRECT ORDER',
    'Garage Confirmed': 'CONFIRMED',
    'Cancelled': 'CANCELLED'
  }

  return (
    <Chip
      label={labels[status]}
      sx={{
        'backgroundColor': colors[status] || '#FF6B35',
        'color': '#fff',
        'fontWeight': 'bold',
        'textAlign': 'center',
        'fontSize': '0.9em',
        'width': '96px',
        'userSelect': 'none',
        'borderRadius': '12px',
        'border': `2px solid ${colors[status] || '#FF6B35'}`,
        'boxShadow': '0 2px 8px rgba(0, 0, 0, 0.1)',
        '& .MuiChip-label': {
          paddingLeft: 1,
          paddingRight: 1
        }
      }}
    />
  )
}

export const DocumentChip = ({ document }) => {
  return (
    <Chip
      label={document}
      sx={{
        backgroundColor: '#34495E',
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
        fontSize: '0.9em',
        padding: '8px 0',
        width: '90px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    />
  )
}

export const RegPlate = ({ children, size = 1 }) => {
  return (
    <div
      className="flex justify-center items-center rounded-md border border-black flex-0"
      style={{
        backgroundColor: 'rgb(248, 208, 56)',
        width: `${160 * size}px`,
        height: `${40 * size}px`,
        fontSize: `${27 * size}px`,
        fontFamily: 'LicensePlate',
        borderColor: 'hsl(47.5, 93.2%, 45%)',
        overflow: 'hidden',
        color: '#000000' // Always black text
      }}
    >
      {children}
    </div>
  )
}

export const ConsumablesBox = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#34495E',
        borderRadius: '12px',
        textAlign: 'center',
        fontWeight: 600,
        color: '#FFFFFF',
        width: 173,
        height: 43,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      CONSUMABLES
    </Box>
  )
}

const MessagesIcon = ({ messageCount, factorUnread, onMessageClick }) => {
  const plural = messageCount !== 1

  return (
    messageCount > 0 && (
      <Tooltip title={`${messageCount} message${plural ? 's' : ''}, ${factorUnread} unread`}>
        {factorUnread > 0 ? (
          <Badge
            badgeContent={`${factorUnread}`}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'white',
                color: '#2ECC71',
                fontWeight: 'bold',
                border: '1px solid #2ECC71'
              }
            }}
          >
            <IconButton onClick={onMessageClick}>
              <MessageOutlined sx={{ color: 'white' }} />
            </IconButton>
          </Badge>
        ) : (
          <IconButton onClick={onMessageClick}>
            <MessageOutlined sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
          </IconButton>
        )}
      </Tooltip>
    )
  )
}

export default EnquiryItem
